/* Salarisadministratie — maandoverzicht + verlof + ziekte + CSV-export.

   Voor superadmin/locatiemanager. Uren komen automatisch uit gepubliceerd rooster
   (na pauze-aftrek). Loonberekening zelf gebeurt door de externe salarisadministratie
   (Loket/Nmbrs); deze pagina exporteert de bron-uren. */
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Download, Clock, Calendar, Activity, Plus, Check, X, Thermometer, Plane, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";

const MAANDEN = ["januari", "februari", "maart", "april", "mei", "juni",
                 "juli", "augustus", "september", "oktober", "november", "december"];

export default function SalarisPage() {
  return (
    <div>
      <PageHeader
        title="Salarisadministratie"
        subtitle="Gewerkte uren, verlof en ziekmeldingen — exporteer maandelijks voor de salarisadministratie."
        testid="salaris-header"
      />
      <Tabs defaultValue="maand" className="w-full">
        <TabsList className="bg-white border border-neutral-200">
          <TabsTrigger value="maand" data-testid="tab-maand"><Clock className="h-4 w-4 mr-2" /> Uren-overzicht</TabsTrigger>
          <TabsTrigger value="verlof" data-testid="tab-verlof"><Plane className="h-4 w-4 mr-2" /> Verlof</TabsTrigger>
          <TabsTrigger value="ziek" data-testid="tab-ziek"><Thermometer className="h-4 w-4 mr-2" /> Ziekmeldingen</TabsTrigger>
        </TabsList>
        <TabsContent value="maand" className="mt-6"><MaandTab /></TabsContent>
        <TabsContent value="verlof" className="mt-6"><VerlofTab /></TabsContent>
        <TabsContent value="ziek" className="mt-6"><ZiekTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function MaandTab() {
  const now = new Date();
  const [jaar, setJaar] = useState(now.getFullYear());
  const [maand, setMaand] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/salaris/maand", { params: { jaar, maand } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [jaar, maand]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    try {
      const r = await api.get("/salaris/export.csv", {
        params: { jaar, maand }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `salaris-${jaar}-${String(maand).padStart(2, "0")}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("CSV-export gedownload");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportInklokCSV = async () => {
    try {
      // WEBSTER KOV — Salaris Auto-Push (CSV variant): download écht ingeklokte
      // (goedgekeurde) uren voor upload naar Nmbrs/Loket. Verschilt van de
      // reguliere export die geplande rooster-uren bevat.
      const r = await api.get("/salaris/inklok-export.csv", {
        params: { jaar, maand }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `salaris-inklok-${jaar}-${String(maand).padStart(2, "0")}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Inklok-export gedownload — upload naar Nmbrs of Loket");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="maand-controls">
        <div>
          <Label>Jaar</Label>
          <Input type="number" value={jaar} onChange={e => setJaar(parseInt(e.target.value) || jaar)} className="w-24" data-testid="salaris-jaar" />
        </div>
        <div>
          <Label>Maand</Label>
          <Select value={String(maand)} onValueChange={v => setMaand(parseInt(v))}>
            <SelectTrigger className="w-40" data-testid="salaris-maand"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {MAANDEN.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1}. {m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button onClick={exportCSV} disabled={busy || !data?.medewerkers?.length}
                data-testid="salaris-export-csv" variant="outline" className="border-neutral-300">
          <Download className="h-4 w-4 mr-1.5" /> Rooster-export
        </Button>
        <Button onClick={exportInklokCSV} disabled={busy}
                data-testid="salaris-export-inklok-csv" style={{ background: "var(--sage)" }} className="text-white">
          <Download className="h-4 w-4 mr-1.5" /> Inklok-export (goedgekeurd)
        </Button>
      </div>
      {data && (
        <Card className="p-4 mb-4 bg-white shadow-none" data-testid="salaris-totalen">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-semibold text-neutral-800" data-testid="totaal-uren">{data.totaal_uren.toFixed(1)}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-widest">Gewerkte uren</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-neutral-800" data-testid="totaal-verlof">{data.totaal_verlof_dagen}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-widest">Verlofdagen</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-neutral-800" data-testid="totaal-ziek">{data.totaal_ziek_dagen}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-widest">Ziektedagen</div>
            </div>
          </div>
        </Card>
      )}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="salaris-table">
          <thead>
            <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="text-left p-3">Medewerker</th>
              <th className="text-left p-3">Functie</th>
              <th className="text-right p-3">Uren</th>
              <th className="text-right p-3">Shifts</th>
              <th className="text-right p-3">Verlof (d)</th>
              <th className="text-right p-3">Ziek (d)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {(data?.medewerkers || []).map(r => (
              <tr key={r.medewerker_id} data-testid={`row-${r.medewerker_id}`}>
                <td className="p-3 font-medium text-neutral-800">{r.naam}</td>
                <td className="p-3 text-neutral-600">{r.functie || "—"}</td>
                <td className="p-3 text-right tabular-nums font-medium">{r.gewerkte_uren.toFixed(2).replace(".", ",")}</td>
                <td className="p-3 text-right tabular-nums text-neutral-500">{r.shift_count}</td>
                <td className="p-3 text-right tabular-nums">{r.verlof_dagen > 0 ? <span className="text-amber-700">{r.verlof_dagen}</span> : <span className="text-neutral-400">0</span>}</td>
                <td className="p-3 text-right tabular-nums">{r.ziek_dagen > 0 ? <span className="text-red-700">{r.ziek_dagen}</span> : <span className="text-neutral-400">0</span>}</td>
              </tr>
            ))}
            {data?.medewerkers?.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Geen medewerkers in deze periode.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VerlofTab() {
  const { user } = useAuth();
  const canWrite = can(user, "staff", "write");
  const [rows, setRows] = useState([]);
  const [meds, setMeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ medewerker_id: "", start: "", eind: "", type: "vakantie", reden: "" });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, m] = await Promise.all([api.get("/salaris/verlof"), api.get("/medewerkers")]);
      setRows(v.data); setMeds(m.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const medMap = useMemo(() => Object.fromEntries(meds.map(m => [m.id, m])), [meds]);

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ medewerker_id: r.medewerker_id, start: r.start, eind: r.eind, type: r.type || "vakantie", reden: r.reden || "" });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditingId(null); setForm({ medewerker_id: "", start: "", eind: "", type: "vakantie", reden: "" }); };
  const submit = async () => {
    if (!form.medewerker_id || !form.start || !form.eind) return toast.error("Vul alle velden in");
    try {
      if (editingId) {
        await api.put(`/salaris/verlof/${editingId}`, form);
        toast.success("Verlof bijgewerkt");
      } else {
        await api.post("/salaris/verlof", form);
        toast.success("Verlof-aanvraag ingediend");
      }
      closeDialog(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeRow = async (r) => {
    if (!window.confirm("Verlof-aanvraag definitief verwijderen?")) return;
    try { await api.delete(`/salaris/verlof/${r.id}`); toast.success("Verwijderd"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const updateStatus = async (vid, status) => {
    const reden = status === "afgewezen" ? window.prompt("Reden van afwijzing?") : null;
    if (status === "afgewezen" && reden === null) return;
    try { await api.post(`/salaris/verlof/${vid}/status`, { status, reden }); toast.success(`Verlof ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUS_TONE = {
    aangevraagd: "bg-amber-50 text-amber-700 border-amber-200",
    goedgekeurd: "bg-emerald-50 text-emerald-700 border-emerald-200",
    afgewezen: "bg-red-50 text-red-700 border-red-200",
    ingetrokken: "bg-neutral-100 text-neutral-600 border-neutral-200",
  };

  return (
    <div>
      {canWrite && (
        <div className="mb-4 flex justify-end">
          <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-verlof-btn" style={{ background: "var(--sage)" }} className="text-white">
                <Plus className="h-4 w-4 mr-1" /> Nieuwe verlof-aanvraag
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader><DialogTitle>{editingId ? "Verlof wijzigen" : "Verlof aanvragen"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Medewerker</Label>
                  <Select value={form.medewerker_id} onValueChange={v => setForm({ ...form, medewerker_id: v })}>
                    <SelectTrigger data-testid="verlof-medewerker"><SelectValue placeholder="Kies medewerker" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {meds.map(m => <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Startdatum</Label><Input type="date" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} data-testid="verlof-start" /></div>
                  <div><Label>Einddatum</Label><Input type="date" value={form.eind} onChange={e => setForm({ ...form, eind: e.target.value })} data-testid="verlof-eind" /></div>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="verlof-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="vakantie">Vakantie</SelectItem>
                      <SelectItem value="plb">PLB (persoonlijk levensfase-budget)</SelectItem>
                      <SelectItem value="onbetaald">Onbetaald verlof</SelectItem>
                      <SelectItem value="ouderschap">Ouderschapsverlof</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Toelichting <span className="text-[11px] text-neutral-500">(optioneel)</span></Label><Textarea value={form.reden} onChange={e => setForm({ ...form, reden: e.target.value })} rows={2} data-testid="verlof-reden" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeDialog}>Annuleren</Button>
                <Button onClick={submit} data-testid="verlof-submit" style={{ background: "var(--sage)" }} className="text-white">{editingId ? "Wijzigingen opslaan" : "Indienen"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="verlof-table">
          <thead>
            <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="text-left p-3">Medewerker</th>
              <th className="text-left p-3">Periode</th>
              <th className="text-right p-3">Dagen</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Geen verlof-aanvragen.</td></tr>}
            {rows.map(v => {
              const m = medMap[v.medewerker_id];
              return (
                <tr key={v.id} data-testid={`verlof-row-${v.id}`}>
                  <td className="p-3 font-medium text-neutral-800">{m ? `${m.voornaam} ${m.achternaam}` : "—"}</td>
                  <td className="p-3 text-neutral-600 tabular-nums">{v.start} → {v.eind}</td>
                  <td className="p-3 text-right tabular-nums">{v.dagen}</td>
                  <td className="p-3 capitalize text-neutral-700">{v.type}</td>
                  <td className="p-3"><Badge variant="outline" className={`border ${STATUS_TONE[v.status] || ""}`}>{v.status}</Badge></td>
                  <td className="p-3 text-right">
                    {canWrite && (
                      <div className="inline-flex gap-1">
                        {v.status === "aangevraagd" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => updateStatus(v.id, "goedgekeurd")} data-testid={`verlof-approve-${v.id}`} className="text-emerald-700 hover:bg-emerald-50 h-7 px-2">
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => updateStatus(v.id, "afgewezen")} data-testid={`verlof-reject-${v.id}`} className="text-red-700 hover:bg-red-50 h-7 px-2">
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`verlof-edit-${v.id}`} className="h-7 px-2">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeRow(v)} data-testid={`verlof-delete-${v.id}`} className="text-red-600 hover:bg-red-50 h-7 px-2">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ZiekTab() {
  const { user } = useAuth();
  const canWrite = can(user, "staff", "write");
  const [rows, setRows] = useState([]);
  const [meds, setMeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ medewerker_id: "", start: new Date().toISOString().slice(0, 10), verwacht_eind: "", reden: "" });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [z, m] = await Promise.all([api.get("/salaris/ziek"), api.get("/medewerkers")]);
      setRows(z.data); setMeds(m.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const medMap = useMemo(() => Object.fromEntries(meds.map(m => [m.id, m])), [meds]);

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ medewerker_id: r.medewerker_id, start: r.start, verwacht_eind: r.verwacht_eind || "", reden: r.reden || "" });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditingId(null); setForm({ medewerker_id: "", start: new Date().toISOString().slice(0, 10), verwacht_eind: "", reden: "" }); };
  const submit = async () => {
    if (!form.medewerker_id || !form.start) return toast.error("Kies medewerker + startdatum");
    try {
      if (editingId) {
        await api.put(`/salaris/ziek/${editingId}`, form);
        toast.success("Ziekmelding bijgewerkt");
      } else {
        await api.post("/salaris/ziek", form);
        toast.success("Ziekmelding geregistreerd");
      }
      closeDialog(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeRow = async (r) => {
    if (!window.confirm("Ziekmelding definitief verwijderen?")) return;
    try { await api.delete(`/salaris/ziek/${r.id}`); toast.success("Verwijderd"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const beter = async (zid) => {
    if (!window.confirm("Medewerker hersteld melden per vandaag?")) return;
    try { await api.post(`/salaris/ziek/${zid}/hersteld`, {}); toast.success("Hersteld"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {canWrite && (
        <div className="mb-4 flex justify-end">
          <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-ziek-btn" className="bg-red-600 hover:bg-red-700 text-white">
                <Thermometer className="h-4 w-4 mr-1" /> Ziekmelden
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader><DialogTitle>{editingId ? "Ziekmelding wijzigen" : "Ziekmelding"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Medewerker</Label>
                  <Select value={form.medewerker_id} onValueChange={v => setForm({ ...form, medewerker_id: v })}>
                    <SelectTrigger data-testid="ziek-medewerker"><SelectValue placeholder="Kies medewerker" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {meds.map(m => <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Ziek vanaf</Label><Input type="date" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} data-testid="ziek-start" /></div>
                  <div><Label>Verwacht hersteld</Label><Input type="date" value={form.verwacht_eind} onChange={e => setForm({ ...form, verwacht_eind: e.target.value })} data-testid="ziek-verwacht" /></div>
                </div>
                <div><Label>Reden <span className="text-[11px] text-neutral-500">(optioneel — privacy)</span></Label><Textarea value={form.reden} onChange={e => setForm({ ...form, reden: e.target.value })} rows={2} data-testid="ziek-reden" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeDialog}>Annuleren</Button>
                <Button onClick={submit} data-testid="ziek-submit" className="bg-red-600 hover:bg-red-700 text-white">{editingId ? "Wijzigingen opslaan" : "Melden"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="ziek-table">
          <thead>
            <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="text-left p-3">Medewerker</th>
              <th className="text-left p-3">Ziek vanaf</th>
              <th className="text-left p-3">Verwacht hersteld</th>
              <th className="text-left p-3">Hersteld op</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Geen ziekmeldingen.</td></tr>}
            {rows.map(z => {
              const m = medMap[z.medewerker_id];
              const isOpen = !z.eind;
              return (
                <tr key={z.id} data-testid={`ziek-row-${z.id}`}>
                  <td className="p-3 font-medium">{m ? `${m.voornaam} ${m.achternaam}` : "—"}</td>
                  <td className="p-3 tabular-nums">{z.start}</td>
                  <td className="p-3 tabular-nums text-neutral-500">{z.verwacht_eind || "—"}</td>
                  <td className="p-3 tabular-nums text-neutral-500">{z.eind || "—"}</td>
                  <td className="p-3">
                    {isOpen
                      ? <Badge variant="outline" className="border bg-red-50 text-red-700 border-red-200">Ziek</Badge>
                      : <Badge variant="outline" className="border bg-emerald-50 text-emerald-700 border-emerald-200">Hersteld</Badge>}
                  </td>
                  <td className="p-3 text-right">
                    {canWrite && (
                      <div className="inline-flex gap-1">
                        {isOpen && (
                          <Button size="sm" variant="ghost" onClick={() => beter(z.id)} data-testid={`ziek-beter-${z.id}`} className="text-emerald-700 h-7">
                            <Check className="h-3.5 w-3.5 mr-1" /> Hersteld
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEdit(z)} data-testid={`ziek-edit-${z.id}`} className="h-7 px-2">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeRow(z)} data-testid={`ziek-delete-${z.id}`} className="text-red-600 hover:bg-red-50 h-7 px-2">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
